import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import PageNavigation from "@/components/PageNavigation";
import SEOHead from "@/components/seo/SEOHead";
import juniorBianchim from "@/assets/junior-bianchim.jpg";
import postCoverColdcalling from "@/assets/cover-cold-calling.jpg";
import mentoria1 from "@/assets/mentoria-1.jpg";
import mentoria2 from "@/assets/mentoria-2.jpg";
import mentoria3 from "@/assets/mentoria-3.jpg";
import mentoria4 from "@/assets/mentoria-4.jpg";
import mentoria6 from "@/assets/mentoria-6.jpg";
import { 
  ArrowRight, 
  Clock, 
  User, 
  Target, 
  Lightbulb, 
  MessageSquare, 
  ThumbsUp,
  Zap,
  Trophy,
  Shield,
  AlertTriangle,
  CheckCircle,
  ChevronRight,
  BookOpen,
  Phone,
  PhoneCall,
  TrendingUp,
  Users,
  Brain,
  Headphones,
  BarChart3,
  Mail,
  Calendar,
  Timer,
  Mic,
  Star,
  XCircle,
  RefreshCw
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const ColdCalling2026 = () => {
  const navigate = useNavigate();

  const evolutionPoints = [
    {
      era: "2010-2015",
      title: "Era do Volume",
      description: "Quanto mais ligações, melhor. Scripts rígidos e foco em quantidade.",
      icon: Phone,
      color: "text-muted-foreground"
    },
    {
      era: "2016-2020",
      title: "Era da Personalização",
      description: "Início da pesquisa prévia. Social Selling ganha força.",
      icon: Users,
      color: "text-primary"
    },
    {
      era: "2021-2024",
      title: "Era Híbrida",
      description: "Multicanalidade: email + LinkedIn + telefone integrados.",
      icon: RefreshCw,
      color: "text-accent"
    },
    {
      era: "2025-2026",
      title: "Era da Inteligência",
      description: "IA potencializa pesquisa, timing e personalização em escala.",
      icon: Brain,
      color: "text-secondary"
    }
  ];

  const mythsVsReality = [
    {
      myth: "Cold calling está morto",
      reality: "78% dos decision-makers atendem ligações de vendedores que demonstram valor. O problema não é a ligação, é a abordagem.",
      icon: XCircle
    },
    {
      myth: "Millennials e Gen Z não atendem telefone",
      reality: "Estudos mostram que 57% dos C-levels preferem telefone para primeiro contato. A questão é relevância, não canal.",
      icon: Phone
    },
    {
      myth: "Email e LinkedIn substituíram o telefone",
      reality: "Esses canais são complementares. O ideal é uma estratégia multicanal onde o telefone acelera conversões.",
      icon: Mail
    },
    {
      myth: "Scripts matam vendas",
      reality: "Scripts são guias, não prisões. Os melhores vendedores dominam o script para poder improvisar com segurança.",
      icon: BookOpen
    }
  ];

  const modernTechniques = [
    {
      number: "01",
      title: "Warm Introduction",
      subtitle: "Aqueça antes de ligar",
      description: "Antes de pegar o telefone, interaja com o prospect em outros canais. Curta posts no LinkedIn, comente conteúdos, envie um email de valor. Quando ligar, você não será um completo desconhecido.",
      example: '"Oi João, vi seu post sobre transformação digital na manufatura. Concordo especialmente com o ponto sobre integração de sistemas. Liguei porque..."',
      tip: "O objetivo não é vender pelo LinkedIn, é criar familiaridade para a ligação",
      stats: "Prospects 'aquecidos' têm 3x mais chance de atender",
      icon: Users
    },
    {
      number: "02",
      title: "Pattern Interrupt",
      subtitle: "Quebre o padrão esperado",
      description: "Os primeiros 5 segundos definem se o prospect vai continuar ou desligar. Em vez do tradicional 'tudo bem?', use aberturas inesperadas que geram curiosidade genuína.",
      example: '"Maria, vou ser honesto: essa é uma ligação de vendas. Posso ter 27 segundos para te mostrar por que vale a pena me ouvir?"',
      tip: "A honestidade desarma. Mentir sobre o motivo da ligação destrói credibilidade instantaneamente",
      stats: "Aberturas honestas aumentam tempo de conversa em 40%",
      icon: Zap
    },
    {
      number: "03",
      title: "Insight-Led Approach",
      subtitle: "Lidere com conhecimento, não com pitch",
      description: "Em vez de começar falando do seu produto, compartilhe um insight relevante sobre o mercado, segmento ou desafio específico do prospect. Posicione-se como consultor, não como vendedor.",
      example: '"Pesquisando empresas do seu segmento, notei que 67% estão enfrentando [problema específico]. Curiosamente, os que resolveram fizeram algo contraintuitivo..."',
      tip: "Invista 10-15 minutos de pesquisa por prospect de alto valor. O ROI é altíssimo",
      stats: "Vendedores que compartilham insights fecham 25% mais",
      icon: Lightbulb
    },
    {
      number: "04",
      title: "Trigger-Based Timing",
      subtitle: "Ligue no momento certo",
      description: "Use ferramentas de sales intelligence para identificar gatilhos de compra: contratações, expansões, mudanças de liderança, captação de investimentos, etc. Ligar após um trigger aumenta relevância exponencialmente.",
      example: '"Vi que a empresa acabou de abrir 15 vagas comerciais. Normalmente, isso significa meta agressiva de crescimento. É isso mesmo?"',
      tip: "Configure alertas do Google, use LinkedIn Sales Navigator e ferramentas como Apollo ou ZoomInfo",
      stats: "Ligações baseadas em triggers têm 3x mais conversão",
      icon: Timer
    },
    {
      number: "05",
      title: "Voice Tonality Mastery",
      subtitle: "Sua voz é sua maior ferramenta",
      description: "Ao telefone, 85% da comunicação é tom de voz. Pratique variação tonal, pausas estratégicas, espelhamento de ritmo. Uma voz confiante mas não arrogante, curiosa mas não invasiva.",
      example: "Use tom ascendente para perguntas abertas, tom descendente para afirmações. Pause após perguntas importantes para dar espaço de reflexão.",
      tip: "Grave suas ligações (com permissão) e analise. A maioria fala rápido demais e não pausa",
      stats: "Vendedores que dominam tonalidade convertem 30% mais",
      icon: Mic
    },
    {
      number: "06",
      title: "Multi-Threading Strategy",
      subtitle: "Não dependa de um único contato",
      description: "Empresas B2B têm em média 6-10 decisores no processo de compra. Identifique e conecte-se com múltiplos stakeholders simultaneamente. Se um não responde, outro pode abrir a porta.",
      example: '"Além do CFO, quem mais está envolvido nessa decisão? Normalmente vemos pessoas de TI, Operações e o próprio CEO participando..."',
      tip: "Mapeie o organograma antes de ligar. LinkedIn é seu melhor amigo aqui",
      stats: "Deals com 3+ contatos têm 340% mais chance de fechar",
      icon: Users
    }
  ];

  const bestTimeToCall = [
    { day: "Segunda", time: "10h-11h / 14h-16h", quality: "Médio", note: "Pessoas organizando a semana" },
    { day: "Terça", time: "9h-11h / 14h-17h", quality: "Alto", note: "Dia mais produtivo" },
    { day: "Quarta", time: "9h-11h / 14h-17h", quality: "Alto", note: "Meio da semana, foco em execução" },
    { day: "Quinta", time: "9h-11h / 14h-16h", quality: "Médio-Alto", note: "Ainda há tempo para decisões" },
    { day: "Sexta", time: "8h-10h", quality: "Baixo", note: "Só primeira hora funciona bem" }
  ];

  const aiTools = [
    {
      category: "Pesquisa e Inteligência",
      tools: ["LinkedIn Sales Navigator", "Apollo.io", "ZoomInfo", "Lusha"],
      description: "Encontre prospects qualificados e colete dados de contato atualizados"
    },
    {
      category: "Análise de Conversas",
      tools: ["Gong", "Chorus", "Jiminny", "Wingman"],
      description: "IA analisa suas ligações e identifica padrões de sucesso"
    },
    {
      category: "Automação Inteligente",
      tools: ["Outreach", "Salesloft", "Reply.io", "Lemlist"],
      description: "Sequências multicanal com personalização em escala"
    },
    {
      category: "Coaching por IA",
      tools: ["Vendas Trainer", "Quantified", "Second Nature"],
      description: "Pratique cold calls com IA antes de ligar para prospects reais"
    }
  ];

  const commonMistakes = [
    {
      mistake: "Falar demais nos primeiros 30 segundos",
      solution: "Faça uma pergunta nos primeiros 10 segundos. Deixe o prospect falar.",
      icon: MessageSquare
    },
    {
      mistake: "Não pesquisar o prospect",
      solution: "Dedique 5-15 minutos de pesquisa. Uma frase personalizada vale mais que 10 genéricas.",
      icon: Target
    },
    {
      mistake: "Desistir após 1-2 tentativas",
      solution: "Em média são necessárias 8 tentativas para conectar. Persista com inteligência.",
      icon: RefreshCw
    },
    {
      mistake: "Focar em features do produto",
      solution: "Foque em dores e resultados. O prospect não quer seu produto, quer resolver um problema.",
      icon: XCircle
    },
    {
      mistake: "Não ter um objetivo claro",
      solution: "Defina o objetivo antes: agendar reunião? Qualificar? Descobrir decisor? Cada ligação tem um propósito.",
      icon: Target
    },
    {
      mistake: "Ignorar objeções",
      solution: "Objeções são oportunidades. Valide, explore e responda com empatia antes de argumentar.",
      icon: Shield
    }
  ];

  const successMetrics = [
    { metric: "Taxa de Conexão", benchmark: "5-15%", description: "Ligações que resultam em conversa real" },
    { metric: "Taxa de Agendamento", benchmark: "10-20%", description: "Conversas que viram reuniões" },
    { metric: "Ligações por Dia", benchmark: "40-60", description: "Volume sustentável com qualidade" },
    { metric: "Tempo Médio de Conversa", benchmark: "3-5 min", description: "Conversa produtiva, não enrolação" }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Cold Calling em 2026: Está Morto ou Ainda Funciona? Guia Completo",
    "description": "Análise completa e atualizada sobre cold calling em 2026. Descubra técnicas modernas, ferramentas de IA e estratégias comprovadas para prospecção por telefone.",
    "author": {
      "@type": "Person",
      "name": "Junior Bianchim"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Bianchim Business"
    },
    "datePublished": "2025-12-10",
    "dateModified": "2025-12-10",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://bianchimbusiness.com.br/posts/cold-calling-2026"
    }
  };

  const faqData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Cold calling ainda funciona em 2026?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Sim, cold calling continua funcionando em 2026, especialmente quando combinado com estratégias modernas de social selling e ferramentas de IA. 78% dos decision-makers ainda atendem ligações de vendedores que demonstram valor relevante."
        }
      },
      {
        "@type": "Question",
        "name": "Qual o melhor horário para fazer cold calls?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Os melhores horários para cold calling são terça e quarta-feira, entre 9h-11h e 14h-17h. Segunda-feira as pessoas estão organizando a semana, e sexta-feira apenas a primeira hora funciona bem."
        }
      },
      {
        "@type": "Question",
        "name": "Quantas ligações devo fazer por dia?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "O benchmark de mercado é 40-60 ligações por dia para manter volume sustentável com qualidade. Mais importante que quantidade é a qualidade da pesquisa prévia e personalização de cada ligação."
        }
      }
    ]
  };

  return (
    <>
      <SEOHead 
        title="Cold Calling em 2026: Está Morto ou Ainda Funciona? Guia Completo"
        description="Análise completa sobre cold calling em 2026. Aprenda técnicas modernas, melhores horários, ferramentas de IA e estratégias comprovadas para prospecção por telefone. +3000 palavras de conteúdo prático."
        keywords="cold calling 2026, cold calling funciona, prospecção telefone, ligação fria, técnicas cold call, vendas outbound, prospecção ativa, SDR, BDR, sales development"
        canonicalUrl="https://bianchimbusiness.com.br/posts/cold-calling-2026"
        ogType="article"
        articlePublishedTime="2024-12-10T00:00:00Z"
        articleAuthor="Junior Bianchim"
      />
      
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqData) }} />

      <div className="min-h-screen bg-gradient-to-br from-background via-background to-accent/10">
        <Header />

        <main id="main-content">
          {/* Hero Section */}
          <article>
            <header className="relative py-12 sm:py-20 md:py-32 overflow-hidden">
              <div 
                className="absolute inset-0 bg-cover bg-center opacity-30"
                style={{ backgroundImage: `url(${postCoverColdcalling})` }}
                role="img"
                aria-label="Imagem de profissional fazendo cold calling"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-background/80 to-background" />
              
              <div className="container mx-auto px-4 relative z-10">
                <div className="max-w-4xl mx-auto text-center">
                  <div className="flex items-center justify-center gap-4 mb-6">
                    <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary/50 bg-primary/10 backdrop-blur-sm">
                      <PhoneCall className="w-4 h-4 text-primary" aria-hidden="true" />
                      <span className="text-sm font-medium text-primary">Prospecção</span>
                    </span>
                    <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-accent/50 bg-accent/10 backdrop-blur-sm">
                      <TrendingUp className="w-4 h-4 text-accent" aria-hidden="true" />
                      <span className="text-sm font-medium text-accent">Atualizado 2026</span>
                    </span>
                  </div>
                  
                  <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold leading-tight mb-6">
                    <span className="bg-gradient-to-r from-primary via-accent to-secondary bg-clip-text text-transparent">
                      Cold Calling em 2026:
                    </span>
                    <br />
                    <span className="text-foreground">Está Morto ou Ainda Funciona?</span>
                  </h1>
                  
                  <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
                    A análise mais completa e atualizada sobre prospecção por telefone. 
                    Descubra se vale a pena investir nessa estratégia e como fazer do jeito certo.
                  </p>

                  <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-primary" aria-hidden="true" />
                      <span>Por <strong className="text-foreground">Junior Bianchim</strong></span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-primary" aria-hidden="true" />
                      <time dateTime="2025-12-10">10 Dez 2025</time>
                      <span>• 15 min de leitura</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <BookOpen className="w-4 h-4 text-primary" aria-hidden="true" />
                      <span>+3.000 palavras</span>
                    </div>
                  </div>
                </div>
              </div>
            </header>

            {/* Intro Section */}
            <section className="py-10 sm:py-16 bg-muted/30" aria-labelledby="intro-heading">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                  <h2 id="intro-heading" className="text-2xl sm:text-3xl font-bold mb-6">
                    A Verdade Sobre Cold Calling em 2026
                  </h2>
                  
                  <div className="prose prose-lg max-w-none text-muted-foreground space-y-4">
                    <p>
                      A cada ano, surgem artigos proclamando a morte do cold calling. <strong className="text-foreground">"Ninguém mais atende telefone"</strong>, dizem. <strong className="text-foreground">"LinkedIn substituiu tudo"</strong>, afirmam. E ainda assim, as empresas que mais crescem continuam investindo pesadamente em equipes de SDRs e BDRs fazendo ligações diárias.
                    </p>
                    
                    <p>
                      A verdade é mais nuançada: o cold calling tradicional — aquele baseado em volume bruto, scripts robóticos e zero personalização — esse sim está morto. Mas o cold calling moderno, potencializado por inteligência artificial, social selling e uma abordagem consultiva, está mais vivo do que nunca.
                    </p>

                    <p>
                      Neste guia completo, vamos analisar dados atualizados, desmistificar mitos persistentes, e entregar técnicas práticas que estão funcionando agora, em 2026. Se você é SDR, BDR, vendedor ou líder comercial, este conteúdo vai transformar a forma como você pensa sobre prospecção ativa.
                    </p>
                  </div>

                  <div className="grid sm:grid-cols-3 gap-4 mt-8">
                    <div className="flex items-center gap-3 p-4 rounded-lg bg-card border border-primary/20">
                      <Phone className="w-8 h-8 text-primary" aria-hidden="true" />
                      <div>
                        <p className="font-bold text-2xl text-primary">78%</p>
                        <p className="text-sm text-muted-foreground">dos C-levels atendem cold calls</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-4 rounded-lg bg-card border border-accent/20">
                      <TrendingUp className="w-8 h-8 text-accent" aria-hidden="true" />
                      <div>
                        <p className="font-bold text-2xl text-accent">8x</p>
                        <p className="text-sm text-muted-foreground">mais efetivo que email frio</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-4 rounded-lg bg-card border border-secondary/20">
                      <Users className="w-8 h-8 text-secondary" aria-hidden="true" />
                      <div>
                        <p className="font-bold text-2xl text-secondary">82%</p>
                        <p className="text-sm text-muted-foreground">dos compradores aceitam reuniões</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Evolução do Cold Calling */}
            <section className="py-12 sm:py-20" aria-labelledby="evolution-heading">
              <div className="container mx-auto px-4">
                <div className="text-center mb-12">
                  <h2 id="evolution-heading" className="text-2xl sm:text-4xl font-bold mb-4">
                    <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                      A Evolução do Cold Calling
                    </span>
                  </h2>
                  <p className="text-muted-foreground max-w-2xl mx-auto">
                    Entenda como chegamos até aqui e por que 2026 representa uma nova era para prospecção ativa.
                  </p>
                </div>

                <div className="max-w-4xl mx-auto">
                  <div className="relative">
                    <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-primary via-accent to-secondary hidden sm:block" />
                    
                    <div className="space-y-8">
                      {evolutionPoints.map((point, index) => {
                        const Icon = point.icon;
                        return (
                          <div key={index} className="relative flex gap-6 items-start">
                            <div className={`w-16 h-16 rounded-full bg-card border-2 border-primary/30 flex items-center justify-center flex-shrink-0 z-10 ${point.color}`}>
                              <Icon className="w-7 h-7" aria-hidden="true" />
                            </div>
                            <div className="flex-1 pb-8">
                              <span className="text-sm font-medium text-primary">{point.era}</span>
                              <h3 className="text-xl font-bold mb-2">{point.title}</h3>
                              <p className="text-muted-foreground">{point.description}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="mt-12 prose prose-lg max-w-none text-muted-foreground space-y-4">
                    <p>
                      A transição mais significativa aconteceu entre 2023 e 2025, quando ferramentas de IA generativa começaram a ser integradas ao processo de vendas. De repente, tornou-se possível fazer pesquisa profunda sobre prospects em segundos, gerar abordagens personalizadas em escala e analisar gravações de ligações para identificar padrões de sucesso.
                    </p>
                    <p>
                      Em 2026, vendedores que ainda usam táticas de 2015 — discando números aleatórios com scripts genéricos — estão fadados ao fracasso. Mas aqueles que abraçaram a nova realidade estão tendo resultados extraordinários. A diferença não está no canal (telefone), mas na metodologia.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Mitos vs Realidade */}
            <section className="py-12 sm:py-20 bg-muted/30" aria-labelledby="myths-heading">
              <div className="container mx-auto px-4">
                <div className="text-center mb-12">
                  <h2 id="myths-heading" className="text-2xl sm:text-4xl font-bold mb-4">
                    <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                      Mitos vs. Realidade
                    </span>
                  </h2>
                  <p className="text-muted-foreground max-w-2xl mx-auto">
                    Vamos desmistificar as crenças mais comuns sobre cold calling.
                  </p>
                </div>

                <div className="max-w-4xl mx-auto space-y-6">
                  {mythsVsReality.map((item, index) => {
                    const Icon = item.icon;
                    return (
                      <Card key={index} className="p-6 border-border">
                        <div className="flex flex-col md:flex-row gap-6">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-3">
                              <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center">
                                <XCircle className="w-5 h-5 text-destructive" aria-hidden="true" />
                              </div>
                              <h3 className="font-bold text-lg">Mito: {item.myth}</h3>
                            </div>
                          </div>
                          <div className="flex-1">
                            <div className="flex items-start gap-3">
                              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                <CheckCircle className="w-5 h-5 text-primary" aria-hidden="true" />
                              </div>
                              <div>
                                <span className="font-medium text-primary text-sm">Realidade:</span>
                                <p className="text-muted-foreground mt-1">{item.reality}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </Card>
                    );
                  })}
                </div>

                <div className="max-w-4xl mx-auto mt-12 prose prose-lg text-muted-foreground">
                  <p>
                    O ponto central é este: <strong className="text-foreground">o telefone não é o problema, a abordagem é</strong>. Quando você liga para alguém sem pesquisa prévia, sem contexto relevante e com um pitch genérico, você merece ser ignorado. Mas quando você demonstra que fez o dever de casa, que entende os desafios específicos daquela pessoa e empresa, e que tem algo genuinamente valioso a oferecer, as portas se abrem.
                  </p>
                </div>
              </div>
            </section>

            {/* Técnicas Modernas */}
            <section className="py-12 sm:py-20" aria-labelledby="techniques-heading">
              <div className="container mx-auto px-4">
                <div className="text-center mb-12">
                  <h2 id="techniques-heading" className="text-2xl sm:text-4xl font-bold mb-4">
                    <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                      6 Técnicas Modernas de Cold Calling
                    </span>
                  </h2>
                  <p className="text-muted-foreground max-w-2xl mx-auto">
                    Estratégias comprovadas que estão funcionando em 2026 para os melhores SDRs do mercado.
                  </p>
                </div>

                <div className="max-w-5xl mx-auto space-y-16">
                  {modernTechniques.map((technique, index) => {
                    const Icon = technique.icon;
                    const isEven = index % 2 === 0;
                    
                    return (
                      <div 
                        key={technique.number}
                        className={`flex flex-col ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-8 items-center`}
                      >
                        <div className="flex-1 w-full">
                          <div className="flex items-center gap-4 mb-4">
                            <span className="text-5xl font-bold text-primary/20">{technique.number}</span>
                            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-accent flex items-center justify-center flex-shrink-0">
                              <Icon className="w-6 h-6 text-white" aria-hidden="true" />
                            </div>
                          </div>
                          <h3 className="text-2xl font-bold mb-2">{technique.title}</h3>
                          <p className="text-lg text-primary mb-4">{technique.subtitle}</p>
                          <p className="text-muted-foreground mb-6">{technique.description}</p>
                          
                          <blockquote className="border-l-4 border-accent pl-4 py-2 mb-6 bg-accent/5 rounded-r-lg">
                            <p className="italic text-foreground">{technique.example}</p>
                          </blockquote>
                          
                          <div className="space-y-3">
                            <div className="flex items-start gap-3 p-4 rounded-lg bg-card border border-primary/20">
                              <Lightbulb className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" aria-hidden="true" />
                              <p className="text-sm text-muted-foreground">
                                <strong className="text-foreground">Dica Pro:</strong> {technique.tip}
                              </p>
                            </div>
                            <div className="flex items-center gap-3 p-3 rounded-lg bg-primary/5 border border-primary/10">
                              <BarChart3 className="w-5 h-5 text-primary flex-shrink-0" aria-hidden="true" />
                              <p className="text-sm font-medium text-primary">{technique.stats}</p>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex-1 w-full max-w-md">
                          <img 
                            src={[mentoria2, mentoria3, mentoria6, juniorBianchim, mentoria2, mentoria3][index]}
                            alt={`Junior Bianchim em mentoria demonstrando a técnica ${technique.title}`}
                            className="rounded-2xl shadow-xl w-full object-cover aspect-[4/3]"
                            loading="lazy"
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </section>

            {/* Melhores Horários */}
            <section className="py-12 sm:py-20 bg-muted/30" aria-labelledby="timing-heading">
              <div className="container mx-auto px-4">
                <div className="text-center mb-12">
                  <h2 id="timing-heading" className="text-2xl sm:text-4xl font-bold mb-4">
                    <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                      Os Melhores Horários para Ligar
                    </span>
                  </h2>
                  <p className="text-muted-foreground max-w-2xl mx-auto">
                    Timing é tudo. Saiba exatamente quando seus prospects estão mais receptivos.
                  </p>
                </div>

                <div className="max-w-4xl mx-auto">
                  <Card className="overflow-hidden border-border">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-muted/50">
                          <tr>
                            <th className="px-6 py-4 text-left font-semibold">Dia</th>
                            <th className="px-6 py-4 text-left font-semibold">Melhores Horários</th>
                            <th className="px-6 py-4 text-center font-semibold">Qualidade</th>
                            <th className="px-6 py-4 text-left font-semibold">Observação</th>
                          </tr>
                        </thead>
                        <tbody>
                          {bestTimeToCall.map((row, index) => (
                            <tr key={index} className="border-t border-border">
                              <td className="px-6 py-4 font-medium">{row.day}</td>
                              <td className="px-6 py-4 text-muted-foreground">{row.time}</td>
                              <td className="px-6 py-4 text-center">
                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                  row.quality === 'Alto' ? 'bg-primary/20 text-primary' :
                                  row.quality === 'Médio-Alto' ? 'bg-accent/20 text-accent' :
                                  row.quality === 'Médio' ? 'bg-secondary/20 text-secondary' :
                                  'bg-muted text-muted-foreground'
                                }`}>
                                  {row.quality}
                                </span>
                              </td>
                              <td className="px-6 py-4 text-muted-foreground text-sm">{row.note}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Card>

                  <div className="mt-8 prose prose-lg max-w-none text-muted-foreground space-y-4">
                    <p>
                      <strong className="text-foreground">Por que terça e quarta são os melhores dias?</strong> Na segunda-feira, executivos estão organizando a semana, respondendo emails acumulados e participando de reuniões de alinhamento. Já na sexta-feira, a mente está no fim de semana. O meio da semana é quando as pessoas estão em modo de execução e mais abertas a novas oportunidades.
                    </p>
                    <p>
                      <strong className="text-foreground">Horário de almoço:</strong> Entre 12h e 14h costuma ser um horário morto. Evite ligar nesse período, a menos que você tenha dados específicos indicando que seu prospect é mais acessível nesse horário.
                    </p>
                    <p>
                      <strong className="text-foreground">Antes das 9h e depois das 17h:</strong> Esses horários podem funcionar excepcionalmente bem para C-levels, que muitas vezes estão no escritório antes ou depois do horário comercial, com menos interrupções. Teste com seu público específico.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Ferramentas de IA */}
            <section className="py-12 sm:py-20" aria-labelledby="tools-heading">
              <div className="container mx-auto px-4">
                <div className="text-center mb-12">
                  <h2 id="tools-heading" className="text-2xl sm:text-4xl font-bold mb-4">
                    <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                      Ferramentas de IA para Cold Calling
                    </span>
                  </h2>
                  <p className="text-muted-foreground max-w-2xl mx-auto">
                    A tecnologia é sua aliada. Estas são as ferramentas que os top performers estão usando em 2026.
                  </p>
                </div>

                <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-6">
                  {aiTools.map((category, index) => (
                    <Card key={index} className="p-6 border-border hover:border-primary/30 transition-colors">
                      <h3 className="text-lg font-bold mb-2">{category.category}</h3>
                      <p className="text-muted-foreground text-sm mb-4">{category.description}</p>
                      <div className="flex flex-wrap gap-2">
                        {category.tools.map((tool, toolIndex) => (
                          <span key={toolIndex} className="px-3 py-1 bg-primary/10 text-primary text-sm rounded-full">
                            {tool}
                          </span>
                        ))}
                      </div>
                    </Card>
                  ))}
                </div>

                <div className="max-w-4xl mx-auto mt-12 prose prose-lg text-muted-foreground space-y-4">
                  <p>
                    A inteligência artificial não vai substituir o vendedor — vai potencializá-lo. Enquanto antes um SDR precisava de 20-30 minutos para pesquisar um prospect adequadamente, hoje ferramentas de IA fazem isso em segundos. O tempo economizado pode ser reinvestido em mais ligações ou em conversas de maior qualidade.
                  </p>
                  <p>
                    Ferramentas de análise de conversas como Gong e Chorus identificam padrões que seriam impossíveis de detectar manualmente: qual abertura gera mais engajamento, quanto tempo de silêncio após objeções correlaciona com fechamento, qual tom de voz funciona melhor para seu ICP. Dados transformam achismo em ciência.
                  </p>
                  <p>
                    <strong className="text-foreground">Destaque especial para ferramentas de coaching por IA:</strong> Plataformas como o Vendas Trainer permitem que SDRs pratiquem cold calls em ambiente simulado antes de ligar para prospects reais. É como um simulador de voo para vendedores — você comete erros sem consequências e recebe feedback instantâneo para melhorar.
                  </p>
                </div>
              </div>
            </section>

            {/* Erros Comuns */}
            <section className="py-12 sm:py-20 bg-muted/30" aria-labelledby="mistakes-heading">
              <div className="container mx-auto px-4">
                <div className="text-center mb-12">
                  <h2 id="mistakes-heading" className="text-2xl sm:text-4xl font-bold mb-4">
                    <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                      6 Erros Fatais no Cold Calling
                    </span>
                  </h2>
                  <p className="text-muted-foreground max-w-2xl mx-auto">
                    Evite estas armadilhas que destroem resultados.
                  </p>
                </div>

                <div className="max-w-5xl mx-auto grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {commonMistakes.map((item, index) => {
                    const Icon = item.icon;
                    return (
                      <Card key={index} className="p-6 border-destructive/20 bg-destructive/5">
                        <div className="flex items-start gap-4 mb-4">
                          <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center flex-shrink-0">
                            <AlertTriangle className="w-5 h-5 text-destructive" aria-hidden="true" />
                          </div>
                          <h3 className="font-bold text-foreground">{item.mistake}</h3>
                        </div>
                        <div className="flex items-start gap-4">
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <CheckCircle className="w-5 h-5 text-primary" aria-hidden="true" />
                          </div>
                          <p className="text-muted-foreground text-sm">{item.solution}</p>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              </div>
            </section>

            {/* Métricas de Sucesso */}
            <section className="py-12 sm:py-20" aria-labelledby="metrics-heading">
              <div className="container mx-auto px-4">
                <div className="text-center mb-12">
                  <h2 id="metrics-heading" className="text-2xl sm:text-4xl font-bold mb-4">
                    <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                      Métricas e Benchmarks 2026
                    </span>
                  </h2>
                  <p className="text-muted-foreground max-w-2xl mx-auto">
                    Saiba se seus números estão dentro do esperado para o mercado atual.
                  </p>
                </div>

                <div className="max-w-4xl mx-auto grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                  {successMetrics.map((item, index) => (
                    <Card key={index} className="p-6 text-center border-border">
                      <p className="text-3xl font-bold text-primary mb-2">{item.benchmark}</p>
                      <h3 className="font-semibold mb-2">{item.metric}</h3>
                      <p className="text-muted-foreground text-sm">{item.description}</p>
                    </Card>
                  ))}
                </div>

                <div className="max-w-4xl mx-auto mt-12 prose prose-lg text-muted-foreground space-y-4">
                  <p>
                    Esses benchmarks são médias de mercado para vendas B2B de ticket médio a alto. Seus números específicos podem variar dependendo do seu segmento, ticket médio e maturidade do mercado. O mais importante é estabelecer sua própria baseline e melhorar consistentemente.
                  </p>
                  <p>
                    <strong className="text-foreground">Dica crucial:</strong> Não foque apenas em volume de ligações. A métrica mais importante é a taxa de conversão de ligações em reuniões qualificadas. Um SDR que faz 30 ligações por dia e marca 6 reuniões é mais valioso que um que faz 80 ligações e marca 4.
                  </p>
                </div>
              </div>
            </section>

            {/* Script Template */}
            <section className="py-12 sm:py-20 bg-muted/30" aria-labelledby="script-heading">
              <div className="container mx-auto px-4">
                <div className="text-center mb-12">
                  <h2 id="script-heading" className="text-2xl sm:text-4xl font-bold mb-4">
                    <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                      Template de Script para 2026
                    </span>
                  </h2>
                  <p className="text-muted-foreground max-w-2xl mx-auto">
                    Um framework flexível para você adaptar ao seu contexto.
                  </p>
                </div>

                <div className="max-w-4xl mx-auto">
                  <Card className="p-8 border-primary/20">
                    <div className="space-y-6">
                      <div>
                        <h3 className="font-bold text-primary mb-2">1. Abertura (5 segundos)</h3>
                        <p className="text-muted-foreground italic bg-muted/50 p-4 rounded-lg">
                          "Oi [Nome], aqui é [Seu Nome] da [Empresa]. Vou ser direto: essa é uma ligação de vendas, mas prometo não roubar seu tempo se não fizer sentido. Posso ter 30 segundos?"
                        </p>
                      </div>
                      
                      <div>
                        <h3 className="font-bold text-primary mb-2">2. Contexto + Insight (15 segundos)</h3>
                        <p className="text-muted-foreground italic bg-muted/50 p-4 rounded-lg">
                          "Vi que a [Empresa do Prospect] está [gatilho específico - contratando, expandindo, lançando produto]. Normalmente quando isso acontece, empresas do seu segmento enfrentam [desafio específico]. Isso ressoa com vocês?"
                        </p>
                      </div>
                      
                      <div>
                        <h3 className="font-bold text-primary mb-2">3. Proposta de Valor (20 segundos)</h3>
                        <p className="text-muted-foreground italic bg-muted/50 p-4 rounded-lg">
                          "A gente ajudou [Empresa Similar] a resolver exatamente isso, e eles conseguiram [resultado quantificável]. Acho que podemos fazer o mesmo para vocês, mas preciso entender melhor o cenário de vocês primeiro."
                        </p>
                      </div>
                      
                      <div>
                        <h3 className="font-bold text-primary mb-2">4. Call to Action (10 segundos)</h3>
                        <p className="text-muted-foreground italic bg-muted/50 p-4 rounded-lg">
                          "Faz sentido a gente conversar 15 minutos essa semana? Quero entender se realmente podemos ajudar antes de propor qualquer coisa."
                        </p>
                      </div>
                    </div>

                    <div className="mt-8 p-4 bg-primary/5 rounded-lg border border-primary/20">
                      <div className="flex items-start gap-3">
                        <Lightbulb className="w-5 h-5 text-primary mt-1 flex-shrink-0" aria-hidden="true" />
                        <div>
                          <p className="font-medium text-foreground mb-1">Lembre-se:</p>
                          <p className="text-sm text-muted-foreground">
                            Este é um framework, não uma prisão. Adapte as palavras para soar natural com sua voz. O mais importante é a estrutura: abertura honesta, contexto relevante, valor claro, CTA específico.
                          </p>
                        </div>
                      </div>
                    </div>
                  </Card>
                </div>
              </div>
            </section>

            {/* Conclusão */}
            <section className="py-12 sm:py-20" aria-labelledby="conclusion-heading">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                  <h2 id="conclusion-heading" className="text-2xl sm:text-3xl font-bold mb-6">
                    Conclusão: Cold Calling Está Mais Vivo do Que Nunca
                  </h2>
                  
                  <div className="prose prose-lg max-w-none text-muted-foreground space-y-4">
                    <p>
                      Chegamos ao final deste guia e a resposta para a pergunta do título é clara: <strong className="text-foreground">cold calling não está morto em 2026, mas evoluiu radicalmente</strong>. O que morreu foi a abordagem preguiçosa, genérica e baseada puramente em volume.
                    </p>
                    
                    <p>
                      O cold calling moderno exige um vendedor diferente: alguém que pesquisa antes de ligar, que lidera com insights em vez de pitches, que usa tecnologia como aliada e que genuinamente se importa em resolver problemas do prospect. É mais trabalhoso? Sim. Mas os resultados são incomparavelmente melhores.
                    </p>

                    <p>
                      Se você é líder comercial, invista em treinamento e ferramentas. Se você é SDR ou BDR, invista em você mesmo. Pratique. Grave suas ligações. Analise. Melhore. Os profissionais que dominarem o cold calling moderno serão os mais valiosos do mercado nos próximos anos.
                    </p>

                    <p>
                      O telefone continua sendo a forma mais rápida de estabelecer conexão humana à distância. Nenhum email, mensagem de LinkedIn ou automação substitui o poder de uma conversa real. Use esse poder com sabedoria.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* CTA Final */}
            <section className="py-12 sm:py-20" aria-labelledby="cta-training">
              <div className="container mx-auto px-4">
                <Card className="p-6 sm:p-12 bg-gradient-to-br from-primary via-accent to-secondary border-0 text-center relative overflow-hidden">
                  <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1552581234-26160f608093?w=1920&q=80')] bg-cover bg-center opacity-10" />
                  <div className="absolute inset-0 bg-gradient-to-r from-primary/90 via-accent/90 to-secondary/90" />
                  
                  <div className="relative z-10">
                    <PhoneCall className="w-12 h-12 sm:w-16 sm:h-16 text-white mx-auto mb-4 sm:mb-6 animate-bounce" aria-hidden="true" />
                    <h2 id="cta-training" className="text-2xl sm:text-4xl md:text-5xl font-bold mb-4 text-white">
                      Pratique Cold Calls Com IA
                    </h2>
                    <p className="text-base sm:text-xl text-white/90 max-w-2xl mx-auto mb-6 sm:mb-8">
                      Antes de ligar para prospects reais, treine no <strong>Vendas Trainer</strong>. Simule ligações, 
                      receba feedback instantâneo e aprimore suas técnicas sem risco.
                    </p>
                    <Button
                      size="lg"
                      onClick={() => navigate("/auth")}
                      className="bg-white text-primary hover:bg-white/90 text-lg px-10 py-6 shadow-2xl"
                    >
                      Começar a Treinar Grátis
                      <ArrowRight className="ml-2 w-5 h-5" aria-hidden="true" />
                    </Button>
                    <p className="text-sm text-white/70 mt-4">
                      ✓ Sem cartão de crédito &nbsp; ✓ Acesso imediato &nbsp; ✓ 10 simulações grátis
                    </p>
                  </div>
                </Card>
              </div>
            </section>

            {/* Author Section */}
            <section className="py-12 sm:py-20 bg-muted/30" aria-labelledby="author-heading">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                  <Card className="p-8 border-border">
                    <div className="flex flex-col sm:flex-row gap-6 items-center sm:items-start">
                      <img 
                        src={juniorBianchim} 
                        alt="Junior Bianchim - Especialista em Vendas"
                        className="w-24 h-24 rounded-full object-cover border-4 border-primary/20"
                      />
                      <div className="text-center sm:text-left">
                        <h3 id="author-heading" className="text-xl font-bold mb-2">Sobre o Autor</h3>
                        <p className="text-lg font-medium text-primary mb-3">Junior Bianchim</p>
                        <p className="text-muted-foreground">
                          Especialista em vendas com mais de 20 anos de experiência no mercado, incluindo atuação na TIM Brasil. 
                          Professor de Vendas, Marketing e Administração no SENAC e CEO da Bianchim Business. Todo conhecimento 
                          foi testado em campo, com foco em resultado e performance comercial.
                        </p>
                      </div>
                    </div>
                  </Card>
                </div>
              </div>
            </section>

            {/* FAQ Section */}
            <section className="py-12 sm:py-20" aria-labelledby="faq-heading">
              <div className="container mx-auto px-4">
                <div className="text-center mb-12">
                  <h2 id="faq-heading" className="text-2xl sm:text-4xl font-bold mb-4">
                    Perguntas Frequentes
                  </h2>
                </div>

                <div className="max-w-3xl mx-auto space-y-4">
                  <Card className="p-6 border-border">
                    <h3 className="font-bold mb-2">Cold calling ainda funciona em 2026?</h3>
                    <p className="text-muted-foreground">
                      Sim, cold calling continua funcionando em 2026, especialmente quando combinado com estratégias modernas de social selling e ferramentas de IA. 78% dos decision-makers ainda atendem ligações de vendedores que demonstram valor relevante.
                    </p>
                  </Card>
                  <Card className="p-6 border-border">
                    <h3 className="font-bold mb-2">Qual o melhor horário para fazer cold calls?</h3>
                    <p className="text-muted-foreground">
                      Os melhores horários são terça e quarta-feira, entre 9h-11h e 14h-17h. Segunda-feira as pessoas estão organizando a semana, e sexta-feira apenas a primeira hora funciona bem.
                    </p>
                  </Card>
                  <Card className="p-6 border-border">
                    <h3 className="font-bold mb-2">Quantas ligações devo fazer por dia?</h3>
                    <p className="text-muted-foreground">
                      O benchmark de mercado é 40-60 ligações por dia para manter volume sustentável com qualidade. Mais importante que quantidade é a qualidade da pesquisa prévia e personalização de cada ligação.
                    </p>
                  </Card>
                </div>
              </div>
            </section>

          </article>
        </main>

        <PageNavigation />

        <Footer />
      </div>
    </>
  );
};

export default ColdCalling2026;
